// noinspection JSUnusedGlobalSymbols

'use client'

import { H4 } from '@expo/html-elements'
import { createActionsheet } from '@gluestack-ui/actionsheet'
import { PrimitiveIcon, UIIcon } from '@gluestack-ui/icon'
import type { VariantProps } from '@gluestack-ui/nativewind-utils'
import { tva } from '@gluestack-ui/nativewind-utils/tva'
import { AnimatePresence, createMotionAnimatedComponent, Motion } from '@legendapp/motion'
import { cssInterop } from 'nativewind'
import React from 'react'
import { FlatList, Pressable, PressableProps, ScrollView, SectionList, Text, View, VirtualizedList } from 'react-native'

const ItemWrapper = React.forwardRef<React.ComponentRef<typeof Pressable>, PressableProps>(({ ...props }, ref) => {
  return (
    <Pressable
      {...props}
      ref={ref}
    />
  )
})

ItemWrapper.displayName = 'ItemWrapper'

const AnimatedPressable = createMotionAnimatedComponent(Pressable)

export const UIActionsheet = createActionsheet({
  Root: View,
  // @ts-ignore
  Content: Motion.View,
  Item: ItemWrapper,
  ItemText: Text,
  DragIndicator: View,
  IndicatorWrapper: View,
  // @ts-ignore
  Backdrop: AnimatedPressable,
  ScrollView: ScrollView,
  VirtualizedList: VirtualizedList,
  FlatList: FlatList,
  SectionList: SectionList,
  SectionHeaderText: H4,
  Icon: UIIcon,
  AnimatePresence: AnimatePresence
})

cssInterop(UIActionsheet, { className: 'style' })
cssInterop(UIActionsheet.Content, { className: 'style' })
cssInterop(ItemWrapper, { className: 'style' })
cssInterop(UIActionsheet.ItemText, { className: 'style' })
cssInterop(UIActionsheet.DragIndicator, { className: 'style' })
cssInterop(UIActionsheet.DragIndicatorWrapper, { className: 'style' })
cssInterop(UIActionsheet.Backdrop, { className: 'style' })
cssInterop(UIActionsheet.ScrollView, {
  className: 'style',
  contentContainerClassName: 'contentContainerStyle',
  indicatorClassName: 'indicatorStyle'
})
cssInterop(UIActionsheet.VirtualizedList, {
  className: 'style',
  ListFooterComponentClassName: 'ListFooterComponentStyle',
  ListHeaderComponentClassName: 'ListHeaderComponentStyle',
  contentContainerClassName: 'contentContainerStyle',
  indicatorClassName: 'indicatorStyle'
})
cssInterop(UIActionsheet.FlatList, {
  className: 'style',
  ListFooterComponentClassName: 'ListFooterComponentStyle',
  ListHeaderComponentClassName: 'ListHeaderComponentStyle',
  columnWrapperClassName: 'columnWrapperStyle',
  contentContainerClassName: 'contentContainerStyle',
  indicatorClassName: 'indicatorStyle'
})
cssInterop(UIActionsheet.SectionList, { className: 'style' })
cssInterop(UIActionsheet.SectionHeaderText, { className: 'style' })

cssInterop(PrimitiveIcon, {
  className: {
    target: 'style',
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: 'classNameColor',
      stroke: true
    }
  }
})

const actionsheetStyle = tva({ base: 'h-full w-full web:pointer-events-none' })

const actionsheetContentStyle = tva({
  base: 'items-center rounded-tl-3xl rounded-tr-3xl border border-b-0 border-outline-100 bg-background-0 p-5 pt-2 shadow-hard-5 web:pointer-events-auto web:select-none'
})

const actionsheetItemStyle = tva({
  base: 'w-full flex-row items-center gap-2 rounded-sm p-3 hover:bg-background-50 active:bg-background-100 data-[focus=true]:bg-background-100 web:data-[focus-visible=true]:bg-background-100 web:data-[focus-visible=true]:outline-indicator-primary data-[disabled=true]:opacity-40 data-[disabled=true]:web:pointer-events-auto data-[disabled=true]:web:cursor-not-allowed'
})

const actionsheetItemTextStyle = tva({
  base: 'font-poppinsRegular text-typography-700',
  variants: {
    isTruncated: {
      true: ''
    },
    bold: {
      true: 'font-poppinsBold'
    },
    underline: {
      true: 'underline'
    },
    strikeThrough: {
      true: 'line-through'
    },
    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl'
    }
  }
})

const actionsheetDragIndicatorStyle = tva({
  base: 'h-1 w-16 rounded-full bg-background-400'
})

const actionsheetDragIndicatorWrapperStyle = tva({
  base: 'w-full items-center py-1'
})

const actionsheetBackdropStyle = tva({
  base: 'absolute bottom-0 left-0 right-0 top-0 bg-background-dark web:pointer-events-auto web:cursor-default'
})

const actionsheetScrollViewStyle = tva({
  base: 'h-auto w-full'
})

const actionsheetVirtualizedListStyle = tva({
  base: 'h-auto w-full'
})

const actionsheetFlatListStyle = tva({
  base: 'h-auto w-full'
})

const actionsheetSectionListStyle = tva({
  base: 'h-auto w-full'
})

const actionsheetSectionHeaderTextStyle = tva({
  base: 'my-0 p-3 font-poppinsBold uppercase leading-5 text-typography-500',
  variants: {
    isTruncated: {
      true: ''
    },
    bold: {
      true: 'font-poppinsBold'
    },
    underline: {
      true: 'underline'
    },
    strikeThrough: {
      true: 'line-through'
    },
    size: {
      '5xl': 'text-5xl',
      '4xl': 'text-4xl',
      '3xl': 'text-3xl',
      '2xl': 'text-2xl',
      xl: 'text-xl',
      lg: 'text-lg',
      md: 'text-base',
      sm: 'text-sm',
      xs: 'text-xs'
    },

    sub: {
      true: 'text-xs'
    },
    italic: {
      true: 'italic'
    },
    highlight: {
      true: 'bg-yellow500'
    }
  },
  defaultVariants: {
    size: 'xs'
  }
})

const actionsheetIconStyle = tva({
  base: 'fill-none text-background-500',
  variants: {
    size: {
      '2xs': 'h-3 w-3',
      xs: 'h-3.5 w-3.5',
      sm: 'h-4 w-4',
      md: 'h-[18px] w-[18px]',
      lg: 'h-5 w-5',
      xl: 'h-6 w-6',
      '2xl': 'h-8 w-8'
    }
  }
})

type IActionsheetProps = VariantProps<typeof actionsheetStyle> & React.ComponentPropsWithoutRef<typeof UIActionsheet>

type IActionsheetContentProps = VariantProps<typeof actionsheetContentStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.Content> & {
    className?: string
  }

type IActionsheetItemProps = VariantProps<typeof actionsheetItemStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.Item>

type IActionsheetItemTextProps = VariantProps<typeof actionsheetItemTextStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.ItemText>

type IActionsheetDragIndicatorProps = VariantProps<typeof actionsheetDragIndicatorStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.DragIndicator>

type IActionsheetDragIndicatorWrapperProps = VariantProps<typeof actionsheetDragIndicatorWrapperStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.DragIndicatorWrapper>

type IActionsheetBackdropProps = VariantProps<typeof actionsheetBackdropStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.Backdrop> & {
    className?: string
  }

type IActionsheetScrollViewProps = VariantProps<typeof actionsheetScrollViewStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.ScrollView>

type IActionsheetVirtualizedListProps = VariantProps<typeof actionsheetVirtualizedListStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.VirtualizedList>

type IActionsheetFlatListProps = VariantProps<typeof actionsheetFlatListStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.FlatList>

type IActionsheetSectionListProps = VariantProps<typeof actionsheetSectionListStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.SectionList>

type IActionsheetSectionHeaderTextProps = VariantProps<typeof actionsheetSectionHeaderTextStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.SectionHeaderText>

type IActionsheetIconProps = VariantProps<typeof actionsheetIconStyle> &
  React.ComponentPropsWithoutRef<typeof UIActionsheet.Icon> & {
    className?: string
    as?: React.ElementType
    height?: number
    width?: number
  }

const Actionsheet = React.forwardRef<React.ComponentRef<typeof UIActionsheet>, IActionsheetProps>(
  ({ className, ...props }, ref) => {
    return (
      <UIActionsheet
        className={actionsheetStyle({
          class: className
        })}
        ref={ref}
        {...props}
      />
    )
  }
)

Actionsheet.displayName = 'Actionsheet'

const ActionsheetContent = React.forwardRef<React.ComponentRef<typeof UIActionsheet.Content>, IActionsheetContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <UIActionsheet.Content
        className={actionsheetContentStyle({
          class: className
        })}
        ref={ref}
        {...props}
      />
    )
  }
)

ActionsheetContent.displayName = 'ActionsheetContent'

const ActionsheetItem = React.forwardRef<React.ComponentRef<typeof UIActionsheet.Item>, IActionsheetItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <UIActionsheet.Item
        className={actionsheetItemStyle({
          class: className
        })}
        ref={ref}
        {...props}
      />
    )
  }
)

ActionsheetItem.displayName = 'ActionsheetItem'

const ActionsheetItemText = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.ItemText>,
  IActionsheetItemTextProps
>(({ isTruncated, bold, underline, strikeThrough, size = 'sm', className, ...props }, ref) => {
  return (
    <UIActionsheet.ItemText
      className={actionsheetItemTextStyle({
        class: className,
        isTruncated,
        bold,
        underline,
        strikeThrough,
        size
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetItemText.displayName = 'ActionsheetItemText'

const ActionsheetDragIndicator = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.DragIndicator>,
  IActionsheetDragIndicatorProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.DragIndicator
      className={actionsheetDragIndicatorStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetDragIndicator.displayName = 'ActionsheetDragIndicator'

const ActionsheetDragIndicatorWrapper = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.DragIndicatorWrapper>,
  IActionsheetDragIndicatorWrapperProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.DragIndicatorWrapper
      className={actionsheetDragIndicatorWrapperStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetDragIndicatorWrapper.displayName = 'ActionsheetDragIndicatorWrapper'

const ActionsheetBackdrop = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.Backdrop>,
  IActionsheetBackdropProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.Backdrop
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 0.5
      }}
      exit={{
        opacity: 0
      }}
      {...props}
      className={actionsheetBackdropStyle({
        class: className
      })}
      ref={ref}
    />
  )
})

ActionsheetBackdrop.displayName = 'ActionsheetBackdrop'

const ActionsheetScrollView = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.ScrollView>,
  IActionsheetScrollViewProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.ScrollView
      className={actionsheetScrollViewStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetScrollView.displayName = 'ActionsheetScrollView'

const ActionsheetVirtualizedList = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.VirtualizedList>,
  IActionsheetVirtualizedListProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.VirtualizedList
      className={actionsheetVirtualizedListStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetVirtualizedList.displayName = 'ActionsheetVirtualizedList'

const ActionsheetFlatList = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.FlatList>,
  IActionsheetFlatListProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.FlatList
      className={actionsheetFlatListStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetFlatList.displayName = 'ActionsheetFlatList'

const ActionsheetSectionList = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.SectionList>,
  IActionsheetSectionListProps
>(({ className, ...props }, ref) => {
  return (
    <UIActionsheet.SectionList
      className={actionsheetSectionListStyle({
        class: className
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetSectionList.displayName = 'ActionsheetSectionList'

const ActionsheetSectionHeaderText = React.forwardRef<
  React.ComponentRef<typeof UIActionsheet.SectionHeaderText>,
  IActionsheetSectionHeaderTextProps
>(({ className, isTruncated, bold, underline, strikeThrough, size, sub, italic, highlight, ...props }, ref) => {
  return (
    <UIActionsheet.SectionHeaderText
      className={actionsheetSectionHeaderTextStyle({
        class: className,
        isTruncated,
        bold,
        underline,
        strikeThrough,
        size,
        sub,
        italic,
        highlight
      })}
      ref={ref}
      {...props}
    />
  )
})

ActionsheetSectionHeaderText.displayName = 'ActionsheetSectionHeaderText'

const ActionsheetIcon = React.forwardRef<React.ComponentRef<typeof UIActionsheet.Icon>, IActionsheetIconProps>(
  ({ className, size = 'sm', ...props }, ref) => {
    if (typeof size === 'number') {
      return (
        <UIActionsheet.Icon
          ref={ref}
          {...props}
          className={actionsheetIconStyle({ class: className })}
          size={size}
        />
      )
    } else if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
      return (
        <UIActionsheet.Icon
          ref={ref}
          {...props}
          className={actionsheetIconStyle({ class: className })}
        />
      )
    }
    return (
      <UIActionsheet.Icon
        className={actionsheetIconStyle({
          class: className,
          size
        })}
        ref={ref}
        {...props}
      />
    )
  }
)

ActionsheetIcon.displayName = 'ActionsheetIcon'

export {
  Actionsheet,
  ActionsheetContent,
  ActionsheetItem,
  ActionsheetItemText,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetBackdrop,
  ActionsheetScrollView,
  ActionsheetVirtualizedList,
  ActionsheetFlatList,
  ActionsheetSectionList,
  ActionsheetSectionHeaderText,
  ActionsheetIcon
}
